import { useEffect, useMemo, useRef, useState } from "react";
import type { CountryLocation } from "../types/domain";
import { findCountry, loadLocations } from "../lib/locations";

type Props = {
  allowAllCountries?: boolean;
  allowRegions?: boolean;
  countryCode: string | null;
  cities: string[];
  regions?: string[];
  onChange: (next: { countryCode: string | null; cities: string[]; regions?: string[]; region?: string | null }) => void;
  className?: string;
  countryLabel?: string;
  cityLabel?: string;
  cityPlaceholder?: string;
  citySelectionMode?: "multi" | "single";
};

type LocationOption =
  | { type: "region"; name: string; rank: number }
  | { type: "city"; name: string; region?: string | null; rank: number };

export function LocationPicker({ allowAllCountries, allowRegions = false, countryCode, cities, regions = [], onChange, className = "", countryLabel, cityLabel, cityPlaceholder = "Все города", citySelectionMode = "multi" }: Props) {
  const [countries, setCountries] = useState<CountryLocation[]>([]);
  const [countryOpen, setCountryOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void loadLocations().then(setCountries).catch((error) => console.warn(error));
  }, []);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setCountryOpen(false);
      setCityOpen(false);
      setQuery("");
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  const selectedCountry = useMemo(() => countryCode ? findCountry(countries, countryCode) : null, [countries, countryCode]);
  const locationOptions = useMemo<LocationOption[]>(() => {
    const normalized = query.trim().toLowerCase();
    const regionSource = allowRegions && citySelectionMode === "multi" ? selectedCountry?.regions ?? [] : [];
    const citySource = selectedCountry?.cities ?? [];
    const regionOptions = regionSource
      .map((region) => {
        const name = region.toLowerCase();
        let rank = 4;
        if (!normalized) rank = 0;
        else if (name === normalized) rank = 0;
        else if (name.startsWith(normalized)) rank = 1;
        else if (name.includes(normalized)) rank = 2;
        return { type: "region" as const, name: region, rank };
      })
      .filter((item) => item.rank < 4)
      .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name, "ru"));
    const cityOptions = citySource
      .map((city) => {
        const name = city.name.toLowerCase();
        const region = String(city.region ?? "").toLowerCase();
        let rank = normalized ? 4 : 2;
        if (normalized && name === normalized) rank = 0;
        else if (normalized && name.startsWith(normalized)) rank = 1;
        else if (normalized && name.includes(normalized)) rank = 2;
        else if (normalized && region.includes(normalized)) rank = 3;
        return { type: "city" as const, name: city.name, region: city.region, rank };
      })
      .filter((item) => item.rank < 4)
      .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name, "ru"));
    return [...regionOptions, ...cityOptions].slice(0, 100);
  }, [allowRegions, citySelectionMode, selectedCountry, query]);

  const toggleCity = (name: string, region?: string | null) => {
    const nextCities = cities.includes(name) ? cities.filter((item) => item !== name) : [...cities, name];
    onChange({ countryCode, cities: nextCities, regions, region: region ?? null });
  };

  const toggleRegion = (name: string) => {
    const nextRegions = regions.includes(name) ? regions.filter((item) => item !== name) : [...regions, name];
    onChange({ countryCode, cities, regions: nextRegions, region: nextRegions[0] ?? null });
  };

  const removeSelectedCity = (name: string) => {
    toggleCity(name);
    setCityOpen(false);
    setQuery("");
  };

  const removeSelectedRegion = (name: string) => {
    toggleRegion(name);
    setCityOpen(false);
    setQuery("");
  };

  const selectCity = (name: string, region?: string | null) => {
    onChange({ countryCode, cities: [name], regions: [], region: region ?? null });
    setCityOpen(false);
    setQuery("");
  };

  const cityDisabled = allowAllCountries && !countryCode;
  const selectedCity = cities[0] ?? null;

  return (
    <div className={`location-picker ${className}`} ref={rootRef}>
      <div className="location-field">
        {countryLabel ? <span className="location-label">{countryLabel}</span> : null}
        <button className="location-trigger country-trigger" type="button" onClick={() => setCountryOpen((value) => !value)}>
          <span>{selectedCountry?.name ?? (allowAllCountries ? "Все страны" : "Выберите страну")}</span>
          <span className="chevron" aria-hidden="true" />
        </button>
        {countryOpen ? (
          <div className="location-menu country-menu">
            <div className="country-options">
              {allowAllCountries ? (
                <button className="country-option" type="button" aria-pressed={!countryCode} onClick={() => { onChange({ countryCode: null, cities: [], regions: [] }); setCountryOpen(false); }}>
                  Все страны
                </button>
              ) : null}
              {countries.map((country) => (
                <button
                  className="country-option"
                  type="button"
                  key={country.code}
                  aria-pressed={country.code === countryCode}
                  onClick={() => { onChange({ countryCode: country.code, cities: [], regions: [] }); setCountryOpen(false); }}
                >
                  {country.name}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      <div className="location-field city-multiselect">
        {cityLabel ? <span className="location-label">{cityLabel}</span> : null}
        {citySelectionMode === "single" ? (
          <button className="location-trigger" type="button" disabled={cityDisabled} onClick={() => setCityOpen((value) => !value)}>
            {selectedCity ? <span className="city-single-value">{selectedCity}</span> : <span className="city-placeholder">{cityPlaceholder}</span>}
            <span className="chevron" aria-hidden="true" />
          </button>
        ) : (
          <div className="location-trigger city-trigger">
            {cities.length || regions.length ? (
              <span className="city-bubbles">
                {regions.slice(0, Math.max(0, 2 - Math.min(cities.length, 2))).map((region) => (
                  <span className="city-bubble" key={`region:${region}`}>
                    <button className="city-bubble-name" type="button" onClick={() => setCityOpen((value) => !value)}>{region}</button>
                    <button type="button" aria-label={`Убрать регион ${region}`} onClick={() => removeSelectedRegion(region)}>×</button>
                  </span>
                ))}
                {cities.slice(0, 2).map((city) => (
                  <span className="city-bubble" key={city}>
                    <button className="city-bubble-name" type="button" onClick={() => setCityOpen((value) => !value)}>{city}</button>
                    <button type="button" aria-label={`Убрать город ${city}`} onClick={() => removeSelectedCity(city)}>×</button>
                  </span>
                ))}
                {cities.length + regions.length > 2 ? <span className="city-bubble">+{cities.length + regions.length - 2}</span> : null}
              </span>
            ) : null}
            <button className="city-trigger-expand" type="button" disabled={cityDisabled} aria-label="Выбрать города и регионы" aria-expanded={cityOpen} onClick={() => setCityOpen((value) => !value)}>
              {!cities.length && !regions.length ? <span className="city-placeholder">{cityPlaceholder}</span> : null}
              <span className="chevron" aria-hidden="true" />
            </button>
          </div>
        )}
        {cityOpen && !cityDisabled ? (
          <div className="location-menu city-menu">
            <div className="city-menu-bubbles" hidden={citySelectionMode === "single" || (!cities.length && !regions.length)}>
              {regions.map((region) => (
                <span className="city-bubble" key={`selected-region:${region}`}><span>{region}</span><button type="button" onClick={() => toggleRegion(region)}>×</button></span>
              ))}
              {cities.map((city) => (
                <span className="city-bubble" key={city}><span>{city}</span><button type="button" onClick={() => toggleCity(city)}>×</button></span>
              ))}
            </div>
            <input type="search" placeholder={allowRegions && citySelectionMode === "multi" ? "Найти город или регион" : "Найти город"} value={query} onChange={(event) => setQuery(event.target.value)} />
            <div className="city-options">
              {locationOptions.map((option) => (
                option.type === "region" ? (
                  <label className="city-option city-option-region" key={`region:${option.name}`}>
                    <input type="checkbox" checked={regions.includes(option.name)} onChange={() => toggleRegion(option.name)} />
                    <span><b>{option.name}</b><small>Регион</small></span>
                  </label>
                ) : citySelectionMode === "single" ? (
                  <button className="city-option city-option-single" type="button" aria-pressed={selectedCity === option.name} key={`${option.name}:${option.region ?? ""}`} onClick={() => selectCity(option.name, option.region)}>
                    <span><b>{option.name}</b>{option.region ? <small>{option.region}</small> : null}</span>
                  </button>
                ) : (
                  <label className="city-option" key={`${option.name}:${option.region ?? ""}`}>
                    <input type="checkbox" checked={cities.includes(option.name)} onChange={() => toggleCity(option.name, option.region)} />
                    <span><b>{option.name}</b>{option.region ? <small>{option.region}</small> : null}</span>
                  </label>
                )
              ))}
              {!locationOptions.length ? <div className="city-option-empty">Локация не найдена</div> : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
